import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { CaseDetails } from '@core/models';
import { PostCalcEntitlement, PostCalcEntitlementRes } from '@core/models/leave-admin/post-calc-entitlement.model';
import { UpdateConfirmationDialog } from '@core/models/shared/update-confirmation-dialog.model';
import { LayoutService } from '@core/services';
import { LeaveAdminStoreService } from '@core/services/leave-admin-store.service';
import { LeaveAdminService } from '@core/services/leave-admin.service';
import { indicate, closeDialogOnEscape } from '@shared/helpers';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { ReplaceBankNamesPipe } from '@shared/pipes/replace-bank-name.pipe';
import { Subject, Observable } from 'rxjs';
import { UpdateConfirmationComponent } from '../update-confirmation/update-confirmation.component';
import { PendingActionsAlertComponent } from '../pending-actions-alert/pending-actions-alert.component';

export interface CalcEntitlementDialogComponentData {
  caseId: number;
  caseStartDate: Date;
  sysText: any;
}

@Component({
    selector: 'app-calc-entitlement-dialog',
    templateUrl: './calc-entitlement-dialog.component.html',
    styleUrl: './calc-entitlement-dialog.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CalcEntitlementDialogComponent {
  static readonly componentName = 'CalcEntitlementDialogComponent';
  
  public position = position;
  public onDragEnded = onDragEnded;
  public isSubmitting$: Subject<boolean> = new Subject<boolean>();
  public confirmResult: PostCalcEntitlementRes = null;
  public cantCalc: boolean = false;
  public missingDetails: string[];

  public isMobile$: Observable<boolean>;

  constructor(@Inject(MAT_DIALOG_DATA)
    public data: CalcEntitlementDialogComponentData,
    private layoutService: LayoutService,
    private dialogRef: MatDialogRef<CalcEntitlementDialogComponent>,
    private leaveAdminService: LeaveAdminService,
    private store: LeaveAdminStoreService,
    private dialog: MatDialog,
    private replaceBankPipe: ReplaceBankNamesPipe
  ) { }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.close());
  }

  public close(): void {
    this.dialogRef.close();
  }

  public onSubmit(confirm: boolean = false, overrideUpdateConf: boolean = false): void {
    if (this.store.isActionRunning) {
      this.openPendingChangesDialog(confirm, overrideUpdateConf);
      return;
    }

    const dto: PostCalcEntitlement = {
      confirm: confirm,
      overrideUpdateConf: overrideUpdateConf,
      caseId: this.data.caseId
    }
    this.leaveAdminService.postCalcEntitlement(dto)
      .pipe(indicate(this.isSubmitting$))
      .subscribe((res)=>{
        if ((res as PostCalcEntitlementRes)?.canCalc === false) {
          this.cantCalc = true;
          this.missingDetails = (res as PostCalcEntitlementRes)?.missingDetails;
        }
        else if ((res as UpdateConfirmationDialog).showUpdateConfirmationDialog) {
          this.openUpdateConfirmationDialog((res as UpdateConfirmationDialog));
        }
        else if (!confirm) {
          this.confirmResult = res as PostCalcEntitlementRes;
        }
        else {
          this.store.caseDetails = res as CaseDetails;
          this.dialogRef.close();
        }
      });
  }

  private openUpdateConfirmationDialog(updateConfirmationDialog: UpdateConfirmationDialog): void {
    const objString = JSON.stringify(updateConfirmationDialog);
    updateConfirmationDialog = JSON
      .parse(this.replaceBankPipe
      .transform(objString, this.store.caseDetails.leaveHoursBanksBuckets));
    const dialogConfig: MatDialogConfig = {
      id: UpdateConfirmationComponent.componentName,
      width: "450px",
      panelClass: "mat-dialog-container-mobileWidth",
      disableClose: false,
      data: {
        sysText: this.store.sysText[this.store.templates.updateConfirmation],
        updateConfirmationDialog: updateConfirmationDialog
      }
    };

    this.dialog.open(UpdateConfirmationComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.onSubmit(true, true);
        } else {
          this.dialogRef.close();
        }
      });
  }

  private openPendingChangesDialog(confirm: boolean, overrideUpdateConf: boolean): void {
    const dialogConfig: MatDialogConfig = {
      id: PendingActionsAlertComponent.componentName,
      width: '300px',
    };

    this.dialog.open(PendingActionsAlertComponent, dialogConfig)
      .beforeClosed()
      .subscribe((res: boolean) => {
      if (res) {
        this.onSubmit(confirm, overrideUpdateConf);
      }
    });
  }
}